if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const saleSubmissionError = this.getMosambiSaleSubmissionError(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        if (saleSubmissionError) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: saleSubmissionError,
            message: 'Cart Error',
          });
          this.handleErrorMessage(saleSubmissionError);
          this.error = false;
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          CartPerformance.measureFromEvent("add:user-action", evt);
          return;
        }

        const { url, requestConfig, responseMeta } = this.buildAddToCartRequest(formData, config);

        fetch(url, requestConfig)
          .then((response) => response.json())
          .then((response) => {
            const parsedResponse = this.normalizeAddToCartResponse(response, responseMeta);

            if (parsedResponse.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: parsedResponse.errors || parsedResponse.description,
                message: parsedResponse.message,
              });
              this.handleErrorMessage(parsedResponse.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: parsedResponse,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(parsedResponse);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(parsedResponse);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getMosambiSaleConfig() {
        return window.mosambiSaleConfig || {};
      }

      getMosambiSaleVariantId() {
        return Number(this.dataset.mosambiSaleVariantId || this.getMosambiSaleConfig().saleVariantId || 0);
      }

      getMosambiFeeVariantId() {
        return Number(this.dataset.mosambiFeeVariantId || this.getMosambiSaleConfig().feeVariantId || 0);
      }

      getMosambiLinkConfig() {
        const config = this.getMosambiSaleConfig();
        return {
          parentKey: config.parentKey || '_mosambi_linked_parent',
          feeFlagKey: config.feeFlagKey || '_mosambi_fee_line',
        };
      }

      getMosambiReservedPropertyKeys() {
        const { parentKey, feeFlagKey } = this.getMosambiLinkConfig();
        return [parentKey, feeFlagKey];
      }

      isMosambiSaleForm() {
        return this.dataset.mosambiSaleForm === 'true';
      }

      isMosambiSaleSubmission(formData) {
        return this.isMosambiSaleForm() && Number(formData.get('id') || 0) === this.getMosambiSaleVariantId();
      }

      getMosambiSaleSubmissionError(formData) {
        if (!this.isMosambiSaleSubmission(formData)) return false;
        if (this.getMosambiFeeVariantId() > 0) return false;

        return 'Mini Memoroids sale fee configuration is incomplete. Please contact support before ordering.';
      }

      buildAddToCartRequest(formData, baseConfig) {
        if (!this.isMosambiSaleSubmission(formData)) {
          baseConfig.body = formData;
          return {
            url: `${routes.cart_add_url}`,
            requestConfig: baseConfig,
            responseMeta: null,
          };
        }

        const bundle = this.buildMosambiSaleBundle(formData);
        const requestConfig = fetchConfig('javascript');
        requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        requestConfig.body = JSON.stringify(this.buildMosambiBundledPayload(formData, bundle.items));

        return {
          url: `${routes.cart_add_url}.js`,
          requestConfig,
          responseMeta: bundle,
        };
      }

      buildMosambiBundledPayload(formData, items) {
        const payload = { items };
        const sections = this.getMosambiBundledSections(formData);
        if (sections.length) payload.sections = sections;

        const sectionsUrl = this.getMosambiBundledSectionsUrl(formData);
        if (sectionsUrl) payload.sections_url = sectionsUrl;

        return payload;
      }

      getMosambiBundledSections(formData) {
        return formData
          .getAll('sections')
          .flatMap((value) => String(value || '').split(','))
          .map((value) => value.trim())
          .filter(Boolean);
      }

      getMosambiBundledSectionsUrl(formData) {
        const sectionsUrl = formData.get('sections_url');
        if (typeof sectionsUrl !== 'string') return '';

        return sectionsUrl.trim();
      }

      buildMosambiSaleBundle(formData) {
        const saleItem = this.buildMosambiSaleItem(formData);
        const linkReference = `mosambi-sale-${saleItem.id}-${Date.now()}`;
        const { parentKey, feeFlagKey } = this.getMosambiLinkConfig();
        const feeVariantId = this.getMosambiFeeVariantId();

        saleItem.properties = {
          ...(saleItem.properties || {}),
          [parentKey]: linkReference,
        };

        return {
          linkReference,
          saleVariantId: saleItem.id,
          items: [
            saleItem,
            {
              id: feeVariantId,
              quantity: saleItem.quantity,
              properties: {
                [parentKey]: linkReference,
                [feeFlagKey]: 'true',
              },
            },
          ],
        };
      }

      buildMosambiSaleItem(formData) {
        const saleItem = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };
        const properties = this.getLineItemProperties(formData);
        const sellingPlan = formData.get('selling_plan');

        if (Object.keys(properties).length) saleItem.properties = properties;
        if (sellingPlan) saleItem.selling_plan = sellingPlan;

        return saleItem;
      }

      getLineItemProperties(formData) {
        const properties = {};
        const reservedKeys = new Set(this.getMosambiReservedPropertyKeys());

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.*)\]$/);
          if (!match) continue;
          if (reservedKeys.has(match[1])) continue;
          properties[match[1]] = value;
        }

        return properties;
      }

      normalizeAddToCartResponse(response, responseMeta) {
        if (!responseMeta || response.status || !Array.isArray(response.items)) return response;

        const { linkReference, saleVariantId } = responseMeta;
        const { parentKey, feeFlagKey } = this.getMosambiLinkConfig();
        const saleLine = response.items.find((item) => {
          const itemProperties = item.properties || {};
          return (
            Number(item.id) === saleVariantId &&
            itemProperties[parentKey] === linkReference &&
            itemProperties[feeFlagKey] !== 'true'
          );
        });

        if (!saleLine) return response;

        return {
          ...response,
          id: saleLine.id,
          key: saleLine.key,
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
